// Define breakpoints in priority order (largest to smallest for proper cascade)
export const BREAKPOINT_ORDER = [
  'isMinWidthDesktopL',
  'isMinWidthDesktop',
  'isMinWidthLaptop',
  'isMinWidthTablet',
  'isMinWidthMobileL',
  'isMinWidthMobileM',
  'isMinWidthMobileS',
  'isMinWidthMobileXs',
  'isMinWidthMobileXxs',
  'isMobileSized',
  'isMinHeightMobileXs',
] as const

type BreakpointRange = (typeof BREAKPOINT_ORDER)[number]

export type BreakpointRangeKeys<T> = { [K in BreakpointRange]?: T }

export type ResponsiveBreakpoints = Required<BreakpointRangeKeys<boolean>>

export type BreakpointValuesWithDefault<T> = BreakpointRangeKeys<T> & { default: T }

/**
 * Get the appropriate value based on current breakpoints.
 * Breakpoints are checked in order from largest to smallest (desktop -> mobile).
 *
 * @param breakpoints Current responsive breakpoints from theme
 * @param breakpointValues Map of breakpoint to value, must include a `default` key
 * @returns The value for the first matching breakpoint, or the default value
 *
 * @example
 * // In a styled component:
 * const Container = styled.View(({ theme }) => ({
 *   paddingTop: getBreakpointValue(theme.breakpoints, {
 *     isMinWidthDesktop: '121px',
 *     isMinWidthTablet: '127px',
 *     isMinWidthMobileL: '48px',
 *     default: '24px',
 *   }),
 * }))
 */
export const getBreakpointValue = <T>(
  breakpoints: ResponsiveBreakpoints,
  breakpointValues: BreakpointValuesWithDefault<T>,
): T => {
  const matchingBreakpoint = BREAKPOINT_ORDER.find(
    (breakpoint) => breakpoints[breakpoint] === true && breakpointValues[breakpoint] !== undefined,
  )

  return matchingBreakpoint ? (breakpointValues[matchingBreakpoint] as T) : breakpointValues.default
}
